/**
 * ActivityCard component - Timeline item style per Pencil design
 */

import { useState, useRef } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { Navigation, Pencil, CalendarRange, CalendarPlus, Trash2, GripVertical, Star } from 'lucide-react';
import type { useSortable } from '@dnd-kit/sortable';
import type { Activity } from '../../types/activity';
import type { Day } from '../../types/trip';
import Dropdown from '../common/Dropdown';
import { getDisplayTime } from '../../utils/activityHelpers';
import { formatDateShort } from '../../utils/dates';
import { getGoogleMapsUrl } from '../../utils/googleMaps';
import { getActivityTypeConfig } from '../../utils/activityTypes';

interface ActivityCardProps {
  activity: Activity;
  index?: number;
  variant?: 'scheduled' | 'unscheduled';
  readOnly?: boolean;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onMoveToDay?: (dayIndex: number) => void;
  onSchedule?: (dayIndex: number) => void;
  onUnschedule?: () => void;
  onTogglePrimary?: () => void;
  availableDays?: Day[];
  dragHandleProps?: ReturnType<typeof useSortable>['listeners'];
}


export default function ActivityCard({
  activity,
  variant = 'scheduled',
  readOnly = false,
  onClick,
  onEdit,
  onDelete,
  onMoveToDay,
  onSchedule,
  onUnschedule,
  onTogglePrimary,
  availableDays,
  dragHandleProps,
}: ActivityCardProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useClickOutside(menuRef, () => setIsMenuOpen(false), isMenuOpen);

  const cfg = getActivityTypeConfig(activity.type);
  const displayTime = getDisplayTime(activity);
  const isUnscheduled = variant === 'unscheduled';

  const dayOptions = availableDays
    ? availableDays.map((day) => ({
        value: day.dayIndex,
        label: `Day ${day.dayNumber} · ${formatDateShort(day.date)}`,
        disabled: day.dayIndex === activity.dayIndex,
      }))
    : [];

  const googleMapsUrl = getGoogleMapsUrl(activity);
  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (googleMapsUrl) window.open(googleMapsUrl, '_blank');
  };

  const hasActions = !readOnly && (onEdit || onDelete || onMoveToDay || onSchedule || onUnschedule);

  const subtitle = [activity.location, cfg.label].filter(Boolean).join(' • ');

  return (
    <div
      className="flex items-start gap-3.5 mb-3.5"
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      {/* Left: type icon circle */}
      <div
        className="shrink-0 flex items-center justify-center rounded-full"
        style={{ width: 44, height: 44, background: cfg.bgColor }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill={cfg.iconColor}>
          <path d={cfg.icon} />
        </svg>
      </div>

      {/* Right: card */}
      <div
        className="flex-1 min-w-0 flex flex-col gap-1.5 rounded-xl bg-white active:scale-[0.98] transition-transform duration-100"
        style={{
          border: activity.isPrimary ? '2px solid #0dccf2' : '1px solid #E8E8E8',
          boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
          padding: '14px 16px',
        }}
      >
        {/* Header row: badge + time + actions */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {activity.isPrimary && (
              <span
                className="shrink-0 flex items-center gap-1 text-[11px] font-bold px-2 py-[3px] rounded-full text-white"
                style={{ background: '#0dccf2' }}
              >
                <Star size={11} fill="#FFFFFF" color="#FFFFFF" />
                主要行程
              </span>
            )}
            <span
              className="text-xs font-bold shrink-0"
              style={{ color: displayTime ? '#0dccf2' : 'transparent' }}
            >
              {displayTime || '—'}
            </span>
          </div>

          <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
            {onTogglePrimary && (
              <button
                onClick={onTogglePrimary}
                className="transition-colors"
                style={{ color: activity.isPrimary ? '#F5B301' : '#DDDDDD' }}
                title={activity.isPrimary ? '取消主要行程' : '標記為主要行程'}
              >
                <Star size={16} fill={activity.isPrimary ? '#F5B301' : 'none'} />
              </button>
            )}

            {!readOnly && (
              <>
              {/* Edit / action bar trigger */}
              {hasActions && (
                <div className="relative">
                  <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="transition-colors"
                    style={{ color: '#DDDDDD' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#94a3b8')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '#DDDDDD')}
                  >
                    <Pencil size={16} />
                  </button>

                  {/* Floating action bar */}
                  {isMenuOpen && (
                      <div
                        ref={menuRef}
                        className="absolute right-0 top-full mt-2 z-50 flex items-center bg-white overflow-hidden"
                        style={{
                          width: 256,
                          height: 45,
                          borderRadius: 14,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                        }}
                      >
                        {/* 編輯 */}
                        {onEdit && (
                          <button
                            onClick={() => { setIsMenuOpen(false); onEdit(); }}
                            className="flex-1 flex items-center justify-center gap-2 h-full hover:bg-slate-50 transition-colors"
                          >
                            <Pencil size={18} color="#6D6C6A" />
                            <span className="text-sm font-semibold" style={{ color: '#3A3A3A' }}>編輯</span>
                          </button>
                        )}

                        {/* 移動 */}
                        {!isUnscheduled && onMoveToDay && dayOptions.length > 0 && (
                          <>
                            <div style={{ width: 1, height: 28, background: '#E8E8E8', flexShrink: 0 }} />
                            <Dropdown
                              className="flex-1 flex h-full"
                              trigger={
                                <button className="w-full flex items-center justify-center gap-2 h-full hover:bg-slate-50 transition-colors">
                                  <CalendarRange size={18} color="#6D6C6A" />
                                  <span className="text-sm font-semibold" style={{ color: '#3A3A3A' }}>移動</span>
                                </button>
                              }
                              options={dayOptions}
                              onSelect={(dayIndex) => {
                                setIsMenuOpen(false);
                                onMoveToDay(dayIndex as number);
                              }}
                            />
                          </>
                        )}

                        {/* 排入 */}
                        {isUnscheduled && onSchedule && dayOptions.length > 0 && (
                          <>
                            <div style={{ width: 1, height: 28, background: '#E8E8E8', flexShrink: 0 }} />
                            <Dropdown
                              className="flex-1 flex h-full"
                              trigger={
                                <button className="w-full flex items-center justify-center gap-2 h-full hover:bg-slate-50 transition-colors">
                                  <CalendarPlus size={18} color="#6D6C6A" />
                                  <span className="text-sm font-semibold" style={{ color: '#3A3A3A' }}>排入</span>
                                </button>
                              }
                              options={dayOptions}
                              onSelect={(dayIndex) => {
                                setIsMenuOpen(false);
                                onSchedule(dayIndex as number);
                              }}
                            />
                          </>
                        )}

                        {/* 刪除 */}
                        {onDelete && (
                          <>
                            <div style={{ width: 1, height: 28, background: '#E8E8E8', flexShrink: 0 }} />
                            <button
                              onClick={() => { setIsMenuOpen(false); onDelete(); }}
                              className="flex-1 flex items-center justify-center gap-2 h-full hover:bg-red-50 transition-colors"
                            >
                              <Trash2 size={18} color="#EF4444" />
                              <span className="text-sm font-semibold" style={{ color: '#EF4444' }}>刪除</span>
                            </button>
                          </>
                        )}
                      </div>
                  )}
                </div>
              )}

              {/* Navigate */}
              {googleMapsUrl && (
                <button
                  onClick={handleNavigate}
                  className="transition-colors"
                  style={{ color: '#DDDDDD' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#94a3b8')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#DDDDDD')}
                >
                  <Navigation size={16} />
                </button>
              )}

              {/* Drag handle */}
              {dragHandleProps && (
                <div
                  {...dragHandleProps}
                  className="cursor-grab touch-none transition-colors"
                  style={{ color: '#DDDDDD' }}
                >
                  <GripVertical size={18} />
                </div>
              )}
              </>
            )}
          </div>
        </div>

        {/* Title */}
        <h3 className="text-base font-semibold leading-tight" style={{ color: '#1A1A1A' }}>
          {activity.name}
        </h3>

        {/* Subtitle */}
        {subtitle && (
          <p className="text-[13px] leading-normal" style={{ color: '#999999' }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}
